const DEFAULT_VERIFY_TIMEOUT_MS = 10000;
const DEFAULT_RETRY_WAIT_MS = 2000;
const MIN_RETRY_WAIT_MS = 250;
const MAX_RETRY_CLICKS = 20;
const MAX_VERIFY_TIMEOUT_MS = 60000;

// Template editor stores these as free text, so "", "abc", "-5" all fall back to the default.
function toNumber(value, fallback) {
  if (value === null || value === undefined || value === true || value === false) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
}

function nonEmptyString(value) {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function resolveClickRetryPolicy(action) {
  const a = action || {};
  const timeoutRaw = toNumber(a.verifyTimeoutMs, DEFAULT_VERIFY_TIMEOUT_MS);
  const verifyTimeoutMs = timeoutRaw > 0 ? Math.min(MAX_VERIFY_TIMEOUT_MS, timeoutRaw) : DEFAULT_VERIFY_TIMEOUT_MS;
  const retryClicks = Math.min(MAX_RETRY_CLICKS, Math.floor(toNumber(a.retryClicks, 0)));
  const waitRaw = toNumber(a.retryWaitMs, DEFAULT_RETRY_WAIT_MS);
  const retryWaitMs = Math.min(verifyTimeoutMs, Math.max(MIN_RETRY_WAIT_MS, waitRaw));
  return {
    verifySelector: nonEmptyString(a.verifySelector),
    verifyElementType: nonEmptyString(a.verifyElementType) || "path",
    verifyTimeoutMs,
    retryClicks,
    retryWaitMs,
    allowCdpEscalation: a.allowCdpEscalation === true,
    disabledSelector: nonEmptyString(a.disabledSelector),
  };
}

// An { error } answer comes from an extension that could not run the probe; treating it as
// a match would let a broken probe report "greyed out" and suppress a legitimate click.
//
// A FOUND element usually does not arrive as a bare boolean: the extension's background
// wraps the answer as { _r, _fp } whenever its resolver captured a fingerprint for the
// element it found (any exact/xpath hit — see background.js execJS and
// eventUtils.lucasResolveElement). Unwrap that envelope; everything else stays unknown.
export function interpretProbe(response) {
  const answer = response && typeof response === "object" && "_r" in response ? response._r : response;
  if (answer === true || answer === false) return answer;
  return null;
}

const UNKNOWN_STATE = { visible: null, disabled: null };

async function readState(getTargetState) {
  try {
    const s = await getTargetState();
    if (!s || typeof s !== "object") return UNKNOWN_STATE;
    return {
      visible: s.visible === true || s.visible === false ? s.visible : null,
      disabled: s.disabled === true || s.disabled === false ? s.disabled : null,
    };
  } catch (e) {
    return UNKNOWN_STATE;
  }
}

export async function runVerifiedClick({ policy, click, getTargetState, pollForElement, isCancelled, log }) {
  const say = typeof log === "function" ? log : () => {};
  const cancelled = typeof isCancelled === "function" ? isCancelled : () => false;
  const fullWait = async (attempts, failReason) => {
    const ok = await pollForElement(policy.verifyTimeoutMs);
    return { ok, attempts, stoppedReason: ok ? "verified" : failReason };
  };

  // Probe before the first click: afterwards a slip the first click DID create can make the
  // menu item legitimately greyed out, which must not be misread as "was disabled".
  // Only `disabled` is read from this probe, so without a disabledSelector it is one
  // wasted extension round trip (plus a resolver fingerprint capture) per click step.
  let initial = UNKNOWN_STATE;
  if (policy.disabledSelector) {
    initial = await readState(getTargetState);
  }
  const disabledAtStart = initial.disabled === true;

  let attempts = 1;
  const first = await click();
  if (first === null) return { ok: false, attempts: 0, stoppedReason: "target_missing" };

  if (disabledAtStart) {
    say("EHR Sync: target greyed out — not re-clicking");
    return fullWait(attempts, "target_disabled");
  }

  while (attempts <= policy.retryClicks) {
    if (await pollForElement(policy.retryWaitMs)) return { ok: true, attempts, stoppedReason: "verified" };
    if (cancelled()) return { ok: false, attempts, stoppedReason: "cancelled" };

    const state = await readState(getTargetState);
    if (state.disabled === true) {
      say("EHR Sync: target greyed out — not re-clicking");
      return fullWait(attempts, "target_disabled");
    }
    // A closed menu means the earlier click most likely landed and the EHR is just slow;
    // clicking again could create a second billing record.
    if (state.visible === false) return fullWait(attempts, "target_hidden");
    if (cancelled()) return { ok: false, attempts, stoppedReason: "cancelled" };

    say("EHR Sync: re-click " + attempts + "/" + policy.retryClicks + " — expected screen not shown yet");
    // A throw here is the extension or the tab going away, not the target hiding. The
    // first click lets that reach the caller's bounded retry; a re-click must do the same
    // rather than downgrade it into a postcondition miss the hook then walks past.
    const res = await click();
    // `attempts` is what the user is told ("Clicked N time(s)"): count landed clicks only.
    if (res === null) return fullWait(attempts, "target_hidden");
    attempts++;
  }

  return fullWait(attempts, "attempts_exhausted");
}
